/**
 * SceneMapEditor - 2D canvas for arranging scenes and creating connections
 *
 * Allows users to:
 * 1. View scenes as draggable thumbnails on a 2D canvas
 * 2. Drag scenes to arrange them spatially
 * 3. Click two scenes to create a bidirectional navigation connection
 * 4. Auto-calculate arrow directions based on relative positions
 *
 * The map positions are saved to each scene's mapPosition field and used
 * to calculate floor-arrow directions for navigation hotspots.
 */

import type { ExperienceScene, HotspotConfig, Vector3Config } from '../src/types/config';
import { showConfirm } from '../src/ui/ModalManager';

// Generate unique ID using browser crypto API
function generateId(): string {
  return crypto.randomUUID();
}

export interface SceneMapEditorOptions {
  /** Scenes to display and arrange */
  scenes: ExperienceScene[];
  /** Callback when scenes are updated (positions, connections) */
  onUpdate: (scenes: ExperienceScene[]) => void;
  /** Callback when user closes the editor */
  onClose: () => void;
}

interface SceneNode {
  scene: ExperienceScene;
  x: number;
  y: number;
  width: number;
  height: number;
  thumbnail?: string;
  thumbnailImage?: HTMLImageElement; // Cached loaded image for rendering
}

interface Connection {
  fromSceneId: string;
  toSceneId: string;
}

export class SceneMapEditor {
  private options: SceneMapEditorOptions;
  private modalElement: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private nodes: SceneNode[] = [];
  private connections: Connection[] = [];
  private initialConnections: Connection[] = []; // Store connections at editor open

  // Interaction state
  private isDragging = false;
  private dragNode: SceneNode | null = null;
  private dragOffset = { x: 0, y: 0 };
  private isConnecting = false;
  private connectFromNode: SceneNode | null = null;
  private selectedNode: SceneNode | null = null;
  private isDraggingConnection = false;
  private mousePos = { x: 0, y: 0 };

  // Canvas settings
  private readonly nodeWidth = 100;
  private readonly nodeHeight = 75;
  private readonly padding = 20;

  constructor(options: SceneMapEditorOptions) {
    this.options = options;
    this.initializeNodes();
    this.initializeConnections();
  }

  /**
   * Open the scene map editor modal
   */
  open(): void {
    this.injectStyles();
    this.createModal();
    this.setupCanvas();
    this.setupEventListeners();
    this.loadThumbnails();
    this.render();

    requestAnimationFrame(() => {
      this.modalElement?.classList.add('visible');
    });
  }

  /**
   * Close the editor
   */
  close(): void {
    if (this.modalElement) {
      this.modalElement.classList.remove('visible');
      setTimeout(() => {
        this.destroy();
      }, 300);
    }
  }

  private initializeNodes(): void {
    const scenes = this.options.scenes;
    const cols = Math.ceil(Math.sqrt(scenes.length));

    this.nodes = scenes.map((scene, index) => {
      // Use saved map position or calculate grid position
      let x: number, y: number;

      if (scene.mapPosition) {
        x = scene.mapPosition.x;
        y = scene.mapPosition.y;
      } else {
        const col = index % cols;
        const row = Math.floor(index / cols);
        x = this.padding + col * (this.nodeWidth + this.padding * 2);
        y = this.padding + row * (this.nodeHeight + this.padding * 2);
      }

      return {
        scene,
        x,
        y,
        width: this.nodeWidth,
        height: this.nodeHeight
      };
    });
  }

  private initializeConnections(): void {
    // Build connections from existing navigation hotspots
    // Each hotspot represents a single-direction connection (A→B is separate from B→A)
    this.connections = [];

    for (const scene of this.options.scenes) {
      if (!scene.hotspots) continue;

      for (const hotspot of scene.hotspots) {
        // Check both targetSceneId and legacy targetStageId
        const targetId = hotspot.targetSceneId || hotspot.targetStageId;

        if (hotspot.type === 'navigation' && targetId) {
          // Check if this exact directional connection already exists
          const exists = this.connections.some(c =>
            c.fromSceneId === scene.id && c.toSceneId === targetId
          );

          if (!exists) {
            this.connections.push({
              fromSceneId: scene.id,
              toSceneId: targetId
            });
          }
        }
      }
    }

    // Store a copy of the initial connections to detect removals on save
    this.initialConnections = this.connections.map(c => ({ ...c }));
  }

  private injectStyles(): void {
    if (document.getElementById('scene-map-editor-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'scene-map-editor-styles';
    style.textContent = `
      .scene-map-editor-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .scene-map-editor-modal.visible {
        opacity: 1;
        visibility: visible;
      }
      .sme-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      .sme-content {
        position: relative;
        width: 95vw;
        max-width: 1200px;
        height: 85vh;
        background: rgba(20, 20, 30, 0.95);
        backdrop-filter: blur(30px);
        -webkit-backdrop-filter: blur(30px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 24px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        transform: scale(0.95);
        transition: transform 0.3s ease;
      }
      .scene-map-editor-modal.visible .sme-content {
        transform: scale(1);
      }
      .sme-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      .sme-header h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
      }
      .sme-header-actions {
        display: flex;
        gap: 12px;
        align-items: center;
      }
      .sme-mode-toggle {
        display: flex;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 2px;
      }
      .sme-mode-btn {
        padding: 6px 12px;
        border: none;
        background: transparent;
        color: rgba(255, 255, 255, 0.7);
        font-size: 13px;
        cursor: pointer;
        border-radius: 6px;
        transition: all 0.2s ease;
      }
      .sme-mode-btn.active {
        background: #007AFF;
        color: white;
      }
      .sme-mode-btn:hover:not(.active) {
        background: rgba(255, 255, 255, 0.1);
      }
      .sme-close-btn {
        background: none;
        border: none;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 4px 8px;
        color: rgba(255, 255, 255, 0.7);
        border-radius: 6px;
        transition: background 0.2s ease;
      }
      .sme-close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      .sme-toolbar {
        padding: 12px 20px;
        background: rgba(0, 0, 0, 0.2);
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .sme-toolbar-hint {
        color: rgba(255, 255, 255, 0.6);
        font-size: 13px;
      }
      .sme-toolbar-hint strong {
        color: #007AFF;
      }
      .sme-canvas-container {
        flex: 1;
        overflow: hidden;
        background:
          linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px),
          linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px);
        background-size: 20px 20px;
        cursor: grab;
      }
      .sme-canvas-container.connecting {
        cursor: crosshair;
      }
      .sme-canvas-container:active {
        cursor: grabbing;
      }
      .sme-canvas {
        display: block;
      }
      .sme-footer {
        padding: 16px 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
      }
      .sme-footer-info {
        color: rgba(255, 255, 255, 0.6);
        font-size: 13px;
      }
      .sme-actions {
        display: flex;
        gap: 12px;
      }
      .sme-btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
      }
      .sme-btn-secondary {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.15);
      }
      .sme-btn-secondary:hover {
        background: rgba(255, 255, 255, 0.15);
      }
      .sme-btn-primary {
        background: #007AFF;
        color: white;
      }
      .sme-btn-primary:hover {
        background: #0066DD;
      }
      .sme-btn-danger {
        background: rgba(255, 59, 48, 0.2);
        color: #FF6B6B;
        border: 1px solid rgba(255, 59, 48, 0.3);
      }
      .sme-btn-danger:hover {
        background: rgba(255, 59, 48, 0.3);
      }
    `;
    document.head.appendChild(style);
  }

  private createModal(): void {
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'scene-map-editor-modal';
    this.modalElement.innerHTML = `
      <div class="sme-backdrop"></div>
      <div class="sme-content">
        <div class="sme-header">
          <h3>Scene Layout Editor</h3>
          <div class="sme-header-actions">
            <div class="sme-mode-toggle">
              <button class="sme-mode-btn active" data-mode="move">Move</button>
              <button class="sme-mode-btn" data-mode="connect">Connect</button>
            </div>
            <button class="sme-close-btn" aria-label="Close">&times;</button>
          </div>
        </div>
        <div class="sme-toolbar">
          <span class="sme-toolbar-hint sme-hint-move"><strong>Drag</strong> scenes to arrange them on the map</span>
          <span class="sme-toolbar-hint sme-hint-connect" style="display: none;"><strong>Drag</strong> from one scene to another to create a bidirectional connection</span>
        </div>
        <div class="sme-canvas-container">
          <canvas class="sme-canvas"></canvas>
        </div>
        <div class="sme-footer">
          <div class="sme-footer-info">
            <span class="sme-scene-count">${this.nodes.length} scenes</span> &bull;
            <span class="sme-connection-count">${this.connections.length} connections</span>
          </div>
          <div class="sme-actions">
            <button class="sme-btn sme-btn-secondary sme-clear-btn">Clear Connections</button>
            <button class="sme-btn sme-btn-secondary sme-cancel-btn">Cancel</button>
            <button class="sme-btn sme-btn-primary sme-save-btn">Save & Generate Portals</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.modalElement);
  }

  private setupCanvas(): void {
    const container = this.modalElement?.querySelector('.sme-canvas-container') as HTMLElement;
    this.canvas = this.modalElement?.querySelector('.sme-canvas') as HTMLCanvasElement;

    if (!container || !this.canvas) return;

    // Set canvas size
    const rect = container.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;

    this.ctx = this.canvas.getContext('2d');

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      if (this.canvas && container) {
        const rect = container.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
        this.render();
      }
    });
    resizeObserver.observe(container);
  }

  private setupEventListeners(): void {
    if (!this.modalElement || !this.canvas) return;

    // Mode toggle
    const modeBtns = this.modalElement.querySelectorAll('.sme-mode-btn');
    modeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        modeBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const mode = (btn as HTMLElement).dataset.mode;
        this.isConnecting = mode === 'connect';

        const container = this.modalElement?.querySelector('.sme-canvas-container');
        container?.classList.toggle('connecting', this.isConnecting);

        // Toggle hint visibility
        const hintMove = this.modalElement?.querySelector('.sme-hint-move') as HTMLElement;
        const hintConnect = this.modalElement?.querySelector('.sme-hint-connect') as HTMLElement;
        if (hintMove) hintMove.style.display = this.isConnecting ? 'none' : 'inline';
        if (hintConnect) hintConnect.style.display = this.isConnecting ? 'inline' : 'none';

        // Reset connection state
        this.connectFromNode = null;
        this.render();
      });
    });

    // Canvas mouse events
    this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.canvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.canvas.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Touch support
    this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd.bind(this));

    // Buttons
    this.modalElement.querySelector('.sme-close-btn')?.addEventListener('click', () => {
      this.options.onClose();
      this.close();
    });

    this.modalElement.querySelector('.sme-cancel-btn')?.addEventListener('click', () => {
      this.options.onClose();
      this.close();
    });

    this.modalElement.querySelector('.sme-clear-btn')?.addEventListener('click', () => {
      this.connections = [];
      this.updateFooterCounts();
      this.render();
    });

    this.modalElement.querySelector('.sme-save-btn')?.addEventListener('click', () => {
      this.saveAndGenerate();
    });

    this.modalElement.querySelector('.sme-backdrop')?.addEventListener('click', () => {
      this.options.onClose();
      this.close();
    });

    // Escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.options.onClose();
      this.close();
    }
  };

  private getMousePos(e: MouseEvent): { x: number; y: number } {
    const rect = this.canvas!.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }

  private getTouchPos(e: TouchEvent): { x: number; y: number } {
    const rect = this.canvas!.getBoundingClientRect();
    const touch = e.touches[0];
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  }

  private findNodeAtPos(x: number, y: number): SceneNode | null {
    // Check in reverse order (topmost first)
    for (let i = this.nodes.length - 1; i >= 0; i--) {
      const node = this.nodes[i];
      if (x >= node.x && x <= node.x + node.width &&
          y >= node.y && y <= node.y + node.height) {
        return node;
      }
    }
    return null;
  }

  private handleMouseDown(e: MouseEvent): void {
    const pos = this.getMousePos(e);
    this.mousePos = pos;
    const node = this.findNodeAtPos(pos.x, pos.y);

    if (this.isConnecting) {
      // In connect mode: start dragging a connection from this node
      if (node) {
        this.connectFromNode = node;
        this.isDraggingConnection = true;
        this.render();
      }
    } else {
      // In move mode: drag the node to reposition it
      if (node) {
        this.isDragging = true;
        this.dragNode = node;
        this.dragOffset = {
          x: pos.x - node.x,
          y: pos.y - node.y
        };

        // Move node to end of array (topmost)
        const index = this.nodes.indexOf(node);
        if (index > -1) {
          this.nodes.splice(index, 1);
          this.nodes.push(node);
        }

        this.selectedNode = node;
        this.render();
      }
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    const pos = this.getMousePos(e);
    this.mousePos = pos;

    if (this.isDraggingConnection && this.connectFromNode) {
      // Dragging a connection line - just re-render to show the preview
      this.render();
    } else if (this.isDragging && this.dragNode) {
      // Dragging a node to reposition
      this.dragNode.x = Math.max(0, Math.min(this.canvas!.width - this.dragNode.width, pos.x - this.dragOffset.x));
      this.dragNode.y = Math.max(0, Math.min(this.canvas!.height - this.dragNode.height, pos.y - this.dragOffset.y));
      this.render();
    }
  }

  private handleMouseUp(e: MouseEvent): void {
    const pos = this.getMousePos(e);

    if (this.isDraggingConnection && this.connectFromNode) {
      // Check if we released over a different node
      const targetNode = this.findNodeAtPos(pos.x, pos.y);
      if (targetNode && targetNode !== this.connectFromNode) {
        // Create the connection
        this.addConnection(this.connectFromNode.scene.id, targetNode.scene.id);
      }
      this.connectFromNode = null;
      this.isDraggingConnection = false;
      this.render();
    }

    this.isDragging = false;
    this.dragNode = null;
  }

  private handleMouseLeave(): void {
    // Cancel any dragging when mouse leaves canvas
    if (this.isDraggingConnection) {
      this.connectFromNode = null;
      this.isDraggingConnection = false;
      this.render();
    }
    this.isDragging = false;
    this.dragNode = null;
  }

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const pos = this.getTouchPos(e);
    this.mousePos = pos;
    const node = this.findNodeAtPos(pos.x, pos.y);

    if (this.isConnecting) {
      // In connect mode: start dragging a connection
      if (node) {
        this.connectFromNode = node;
        this.isDraggingConnection = true;
        this.render();
      }
    } else if (node) {
      // In move mode: drag the node
      this.isDragging = true;
      this.dragNode = node;
      this.dragOffset = {
        x: pos.x - node.x,
        y: pos.y - node.y
      };
      this.selectedNode = node;
      this.render();
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();
    const pos = this.getTouchPos(e);
    this.mousePos = pos;

    if (this.isDraggingConnection && this.connectFromNode) {
      this.render();
    } else if (this.isDragging && this.dragNode) {
      this.dragNode.x = Math.max(0, Math.min(this.canvas!.width - this.dragNode.width, pos.x - this.dragOffset.x));
      this.dragNode.y = Math.max(0, Math.min(this.canvas!.height - this.dragNode.height, pos.y - this.dragOffset.y));
      this.render();
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (this.isDraggingConnection && this.connectFromNode) {
      // Check if we released over a different node
      const targetNode = this.findNodeAtPos(this.mousePos.x, this.mousePos.y);
      if (targetNode && targetNode !== this.connectFromNode) {
        this.addConnection(this.connectFromNode.scene.id, targetNode.scene.id);
      }
      this.connectFromNode = null;
      this.isDraggingConnection = false;
      this.render();
    }

    this.isDragging = false;
    this.dragNode = null;
  }

  private addConnection(fromId: string, toId: string): void {
    // Check if this exact directional connection already exists
    // (A→B is different from B→A, allowing separate control over each direction)
    const exists = this.connections.some(c =>
      c.fromSceneId === fromId && c.toSceneId === toId
    );

    if (!exists) {
      this.connections.push({ fromSceneId: fromId, toSceneId: toId });
      this.updateFooterCounts();
    }
  }

  private updateFooterCounts(): void {
    const countEl = this.modalElement?.querySelector('.sme-connection-count');
    if (countEl) {
      countEl.textContent = `${this.connections.length} connections`;
    }
  }

  private async loadThumbnails(): Promise<void> {
    // Load all thumbnails in parallel for better performance
    const loadPromises = this.nodes.map(async (node) => {
      const skyboxUrl = node.scene.skybox?.url || node.scene.skybox?.thumbnailUrl;
      if (skyboxUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              node.thumbnail = skyboxUrl;
              node.thumbnailImage = img; // Cache the loaded image object
              resolve();
            };
            img.onerror = reject;
            img.src = skyboxUrl;
          });
        } catch {
          // Use placeholder if image fails to load
        }
      }
    });

    // Wait for all images to load, then render once
    await Promise.all(loadPromises);
    this.render();
  }

  private render(): void {
    if (!this.ctx || !this.canvas) return;

    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Clear
    ctx.clearRect(0, 0, w, h);

    // Draw connections (single-direction arrows)
    // Each connection A→B is separate from B→A, giving control over each direction
    ctx.lineWidth = 2;

    for (const conn of this.connections) {
      const fromNode = this.nodes.find(n => n.scene.id === conn.fromSceneId);
      const toNode = this.nodes.find(n => n.scene.id === conn.toSceneId);

      if (fromNode && toNode) {
        const fromX = fromNode.x + fromNode.width / 2;
        const fromY = fromNode.y + fromNode.height / 2;
        const toX = toNode.x + toNode.width / 2;
        const toY = toNode.y + toNode.height / 2;

        // Check if reverse connection exists (for visual offset)
        const hasReverse = this.connections.some(c =>
          c.fromSceneId === conn.toSceneId && c.toSceneId === conn.fromSceneId
        );

        // Calculate line angle
        const angle = Math.atan2(toY - fromY, toX - fromX);
        const arrowSize = 10;
        const nodeOffset = 40; // Offset from node edge

        // If bidirectional, offset the lines so they don't overlap
        const perpOffset = hasReverse ? 6 : 0;
        const perpAngle = angle + Math.PI / 2;
        const offsetX = perpOffset * Math.cos(perpAngle);
        const offsetY = perpOffset * Math.sin(perpAngle);

        const startX = fromX + nodeOffset * Math.cos(angle) + offsetX;
        const startY = fromY + nodeOffset * Math.sin(angle) + offsetY;
        const endX = toX - nodeOffset * Math.cos(angle) + offsetX;
        const endY = toY - nodeOffset * Math.sin(angle) + offsetY;

        // Draw line
        ctx.strokeStyle = 'rgba(0, 122, 255, 0.7)';
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        // Draw arrowhead at "to" end only (single direction)
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - arrowSize * Math.cos(angle - Math.PI / 6),
          endY - arrowSize * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          endX - arrowSize * Math.cos(angle + Math.PI / 6),
          endY - arrowSize * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fillStyle = 'rgba(0, 122, 255, 0.9)';
        ctx.fill();
      }
    }

    // Draw connecting line preview while dragging
    if (this.isDraggingConnection && this.connectFromNode) {
      const fromX = this.connectFromNode.x + this.connectFromNode.width / 2;
      const fromY = this.connectFromNode.y + this.connectFromNode.height / 2;

      // Check if hovering over a valid target node
      const hoverNode = this.findNodeAtPos(this.mousePos.x, this.mousePos.y);
      const isValidTarget = hoverNode && hoverNode !== this.connectFromNode;

      ctx.strokeStyle = isValidTarget ? 'rgba(0, 255, 100, 0.8)' : 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(this.mousePos.x, this.mousePos.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw arrow at the end
      const angle = Math.atan2(this.mousePos.y - fromY, this.mousePos.x - fromX);
      const arrowSize = 10;
      ctx.beginPath();
      ctx.moveTo(this.mousePos.x, this.mousePos.y);
      ctx.lineTo(
        this.mousePos.x - arrowSize * Math.cos(angle - Math.PI / 6),
        this.mousePos.y - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        this.mousePos.x - arrowSize * Math.cos(angle + Math.PI / 6),
        this.mousePos.y - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fillStyle = isValidTarget ? 'rgba(0, 255, 100, 0.8)' : 'rgba(255, 255, 255, 0.5)';
      ctx.fill();
    }

    // Draw nodes
    for (const node of this.nodes) {
      const isSelected = node === this.selectedNode;
      const isConnectFrom = node === this.connectFromNode;

      // Background
      ctx.fillStyle = isSelected || isConnectFrom ? 'rgba(0, 122, 255, 0.3)' : 'rgba(40, 40, 50, 0.9)';
      ctx.strokeStyle = isSelected || isConnectFrom ? '#007AFF' : 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = isSelected || isConnectFrom ? 2 : 1;

      this.roundRect(ctx, node.x, node.y, node.width, node.height, 8);
      ctx.fill();
      ctx.stroke();

      // Thumbnail - use cached image object to avoid flickering
      if (node.thumbnailImage) {
        ctx.save();
        this.roundRect(ctx, node.x + 4, node.y + 4, node.width - 8, node.height - 24, 4);
        ctx.clip();
        ctx.drawImage(node.thumbnailImage, node.x + 4, node.y + 4, node.width - 8, node.height - 24);
        ctx.restore();
      } else {
        // Placeholder - shown while image is loading
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        this.roundRect(ctx, node.x + 4, node.y + 4, node.width - 8, node.height - 24, 4);
        ctx.fill();
      }

      // Scene name
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      const name = node.scene.name || 'Untitled';
      const truncatedName = name.length > 12 ? name.substring(0, 11) + '...' : name;
      ctx.fillText(truncatedName, node.x + node.width / 2, node.y + node.height - 6);
    }
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  private saveAndGenerate(): void {
    // Check if any initial connections were removed
    const removedConnections = this.initialConnections.filter(initial =>
      !this.connections.some(c =>
        c.fromSceneId === initial.fromSceneId && c.toSceneId === initial.toSceneId
      )
    );

    if (removedConnections.length > 0) {
      // Show warning and confirm before proceeding
      showConfirm(
        `${removedConnections.length} connection${removedConnections.length > 1 ? 's were' : ' was'} removed. The associated navigation portals and floor arrows will be deleted. Continue?`,
        'Connections Removed',
        '⚠️',
        'Save Changes',
        'Cancel'
      ).then((confirmed) => {
        if (confirmed) {
          this.doSaveAndGenerate();
        }
      });
    } else {
      // No connections removed, save directly
      this.doSaveAndGenerate();
    }
  }

  private doSaveAndGenerate(): void {
    // Update scene map positions
    for (const node of this.nodes) {
      node.scene.mapPosition = {
        x: node.x,
        y: node.y
      };
    }

    // Build a set of valid connections for quick lookup
    // Format: "fromSceneId->toSceneId"
    const validConnections = new Set<string>();
    for (const conn of this.connections) {
      validConnections.add(`${conn.fromSceneId}->${conn.toSceneId}`);
    }

    // Remove navigation portals that no longer have connections
    for (const scene of this.options.scenes) {
      if (!scene.hotspots) continue;

      scene.hotspots = scene.hotspots.filter(hotspot => {
        // Keep non-navigation hotspots (info, media, etc.)
        if (hotspot.type !== 'navigation') return true;

        // Get the target scene ID (check both new and legacy field names)
        const targetId = hotspot.targetSceneId || hotspot.targetStageId;
        if (!targetId) return true; // Keep if no target (shouldn't happen, but safe)

        // Check if this connection still exists
        const connectionKey = `${scene.id}->${targetId}`;
        return validConnections.has(connectionKey);
      });
    }

    // Generate navigation hotspots from connections
    for (const conn of this.connections) {
      const fromScene = this.options.scenes.find(s => s.id === conn.fromSceneId);
      const toScene = this.options.scenes.find(s => s.id === conn.toSceneId);

      if (!fromScene || !toScene) continue;

      const fromNode = this.nodes.find(n => n.scene.id === conn.fromSceneId);
      const toNode = this.nodes.find(n => n.scene.id === conn.toSceneId);

      if (!fromNode || !toNode) continue;

      // Calculate arrow direction from positions
      const fromCenter = { x: fromNode.x + fromNode.width / 2, y: fromNode.y + fromNode.height / 2 };
      const toCenter = { x: toNode.x + toNode.width / 2, y: toNode.y + toNode.height / 2 };

      // Create hotspot only in the FROM scene (single direction)
      // User explicitly creates A→B and B→A as separate connections if they want both
      this.createNavigationHotspot(fromScene, toScene, fromCenter, toCenter);
    }

    this.options.onUpdate(this.options.scenes);
    this.close();
  }

  private createNavigationHotspot(
    fromScene: ExperienceScene,
    toScene: ExperienceScene,
    fromPos: { x: number; y: number },
    toPos: { x: number; y: number }
  ): void {
    // Check if portal to this target already exists (check both new and legacy field names)
    const existingPortal = fromScene.hotspots?.find(
      h => h.type === 'navigation' &&
           (h.targetSceneId === toScene.id || h.targetStageId === toScene.id)
    );

    // Calculate 3D position from the 2D map layout
    // The 2D canvas represents a bird's-eye view where:
    // - TOP of preview = camera's forward direction (where user looks initially)
    // - BOTTOM of preview = behind the camera
    // - LEFT/RIGHT = left/right in 3D space
    const dx = toPos.x - fromPos.x;
    const dy = toPos.y - fromPos.y;
    const distance2D = Math.sqrt(dx * dx + dy * dy);

    // Distance from center to place portal - should be near dome edge
    // Dome radius is ~500 units, so place portals at ~420 units (leaving margin)
    const portalDistance = 420;

    // Floor Y level - typical floor is around -130 to -150 in scene coordinates
    // This positions the arrow on the ground plane
    const floorY = -130;

    // Direction vector (normalized)
    const dirX = distance2D > 0 ? dx / distance2D : 0;
    const dirY = distance2D > 0 ? dy / distance2D : 1;

    // Map 2D canvas coords to 3D world coords
    // Canvas: +X is right, +Y is DOWN (increases downward)
    // 3D World: Camera looks toward -Z, +X is right
    //
    // CORRECTED Mapping:
    // - Canvas vertical (dy) → 3D X axis
    // - Canvas horizontal (dx) → 3D Z axis
    // - Canvas DOWN (+Y) → 3D +X (right side when facing forward)
    // - Canvas UP (-Y) → 3D -X (left side)
    // - Canvas RIGHT (+X) → 3D +Z (backward)
    // - Canvas LEFT (-X) → 3D -Z (forward)
    const portalPosition: Vector3Config = {
      x: dirY * portalDistance,            // Canvas vertical → 3D left/right
      y: floorY,                           // Floor level
      z: dirX * portalDistance             // Canvas horizontal → 3D forward/back
    };

    // Arrow direction values:
    // - 0° = points toward camera/viewer (inward) - default for new arrows
    // - 180° = points away from camera (outward)
    // The portal is positioned based on the destination scene's relative location
    const normalizedArrowDir = 0;

    if (existingPortal) {
      // Update existing portal's position and direction
      existingPortal.arrowDirection = normalizedArrowDir;
      existingPortal.position = portalPosition;
      // Migrate legacy field to new field
      if (existingPortal.targetStageId && !existingPortal.targetSceneId) {
        existingPortal.targetSceneId = existingPortal.targetStageId;
      }
      return;
    }

    // Create new portal
    const portal: HotspotConfig = {
      id: generateId(),
      type: 'navigation',
      position: portalPosition,
      targetSceneId: toScene.id,
      portalEffectStyle: 'floor-arrow',
      arrowDirection: normalizedArrowDir,
      label: toScene.name || 'Next'
    };

    if (!fromScene.hotspots) {
      fromScene.hotspots = [];
    }

    fromScene.hotspots.push(portal);
  }

  private destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.modalElement?.remove();
    this.modalElement = null;
    this.canvas = null;
    this.ctx = null;
  }
}

/**
 * Helper function to open the scene map editor
 */
export function openSceneMapEditor(options: SceneMapEditorOptions): SceneMapEditor {
  const editor = new SceneMapEditor(options);
  editor.open();
  return editor;
}
